import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, FileText, Loader2, GraduationCap, BookOpen } from "lucide-react";

const CLASS_TOPICS = {
  "1-5": {
    label: "Class 1–5 (Foundation)",
    topics: {
      "Numbers & Counting": ["Counting Objects", "Number Names", "Comparing Numbers", "Ordering Numbers"],
      "Place Value": ["Tens and Ones", "Hundreds", "Thousands", "Expanded Form"],
      "Addition & Subtraction": ["Single Digit", "Double Digit", "With Carry/Borrow", "Word Problems"],
      "Multiplication & Division": ["Tables", "Single Digit", "Multi Digit", "Long Division"],
      "Fractions": ["Introduction", "Like Fractions", "Comparing Fractions", "Simple Operations"],
      "Measurement": ["Length", "Weight", "Capacity", "Unit Conversion"],
      "Time": ["Reading Clock", "AM/PM", "Calendar", "Duration"],
      "Money": ["Coins & Notes", "Addition", "Subtraction", "Making Change"],
      "Shapes & Geometry": ["2D Shapes", "3D Shapes", "Symmetry", "Perimeter"],
      "Patterns": ["Number Patterns", "Shape Patterns", "Growing Patterns"],
      "Data Handling": ["Pictograph", "Bar Graph", "Tally Marks"],
      "Word Problems": ["Addition", "Subtraction", "Multiplication", "Mixed Operations"]
    }
  },
  "6-8": {
    label: "Class 6–8 (Middle School)",
    topics: {
      "Integers": ["Number Line", "Addition & Subtraction", "Multiplication & Division", "Word Problems"],
      "Fractions & Decimals": ["Operations on Fractions", "Decimal Operations", "Conversion", "Word Problems"],
      "Ratio & Proportion": ["Simplifying Ratios", "Direct Proportion", "Inverse Proportion", "Unitary Method"],
      "Percentage": ["Conversion", "Finding Percentage", "Increase & Decrease", "Word Problems"],
      "Algebra": ["Variables & Expressions", "Simplification", "Factorization", "Identities"],
      "Linear Equations": ["One Variable", "Word Problems", "Solving Equations"],
      "Geometry": ["Lines & Angles", "Triangles", "Quadrilaterals", "Circles"],
      "Mensuration": ["Area", "Perimeter", "Volume", "Surface Area"],
      "Data Handling & Statistics": ["Mean, Median, Mode", "Bar Graph", "Pie Chart", "Frequency Distribution"],
      "Probability": ["Basic Probability", "Events", "Experiments"],
      "Speed, Time & Distance": ["Formula Based", "Relative Speed", "Word Problems"],
      "Simple Interest": ["Formula Based", "Finding Principal", "Finding Rate", "Finding Time"]
    }
  },
  "9-10": {
    label: "Class 9–10 (Secondary)",
    topics: {
      "Number Systems": ["Rational Numbers", "Irrational Numbers", "Real Numbers", "Laws of Exponents"],
      "Polynomials": ["Degree & Types", "Zeroes", "Factorization", "Remainder Theorem"],
      "Linear Equations in Two Variables": ["Graphical Method", "Substitution", "Elimination", "Word Problems"],
      "Quadratic Equations": ["Factorization", "Quadratic Formula", "Nature of Roots", "Word Problems"],
      "Arithmetic Progressions": ["nth Term", "Sum of n Terms", "Word Problems"],
      "Coordinate Geometry": ["Distance Formula", "Section Formula", "Area of Triangle", "Slope"],
      "Trigonometry": ["Ratios", "Complementary Angles", "Heights & Distances", "Identities"],
      "Circles": ["Tangents", "Chords", "Arc & Sector", "Theorems"],
      "Triangles": ["Similarity", "Congruence", "Pythagoras Theorem", "BPT"],
      "Mensuration": ["Surface Area", "Volume", "Combination of Solids", "Frustum"],
      "Statistics": ["Mean", "Median", "Mode", "Ogive"],
      "Probability": ["Classical Probability", "Complementary Events", "Word Problems"],
      "Profit & Loss": ["Basic Problems", "Discount", "Successive Discount", "Marked Price"],
      "GST / Tax": ["CGST & SGST", "Tax Calculation", "Word Problems"],
      "Banking Basics": ["Simple Interest", "Compound Interest", "EMI Basics"]
    }
  },
  "11-12": {
    label: "Class 11–12 (Senior Secondary)",
    topics: {
      "Sets": ["Types of Sets", "Venn Diagrams", "Operations", "Word Problems"],
      "Relations & Functions": ["Types of Relations", "Types of Functions", "Domain & Range", "Composition"],
      "Complex Numbers": ["Operations", "Modulus & Argument", "Polar Form", "Square Root"],
      "Matrices": ["Types", "Operations", "Transpose", "Symmetric & Skew-Symmetric"],
      "Determinants": ["Expansion", "Properties", "Inverse of Matrix", "System of Equations"],
      "Sequences & Series": ["AP", "GP", "HP", "Sum to n Terms"],
      "Limits": ["Algebraic Limits", "Trigonometric Limits", "L'Hopital Rule"],
      "Derivatives": ["First Principles", "Rules of Differentiation", "Chain Rule", "Implicit Differentiation"],
      "Applications of Derivatives": ["Rate of Change", "Tangent & Normal", "Maxima & Minima", "Increasing/Decreasing"],
      "Integrals": ["Indefinite Integrals", "Definite Integrals", "Integration Methods", "Applications"],
      "Differential Equations": ["Order & Degree", "Variable Separable", "Homogeneous", "Linear"],
      "Straight Lines": ["Slope", "Equations of Line", "Distance", "Intersection"],
      "Conic Sections": ["Circle", "Parabola", "Ellipse", "Hyperbola"],
      "3D Geometry": ["Direction Cosines", "Equation of Line", "Equation of Plane", "Distance"],
      "Vectors": ["Types", "Operations", "Dot Product", "Cross Product"],
      "Trigonometric Functions": ["Graphs", "Domain & Range", "General Solutions"],
      "Trigonometric Identities": ["Sum & Difference", "Multiple Angles", "Product to Sum"],
      "Inverse Trigonometry": ["Principal Values", "Properties", "Simplification"],
      "Probability": ["Conditional Probability", "Bayes Theorem", "Binomial Distribution", "Random Variables"],
      "Statistics": ["Variance", "Standard Deviation", "Correlation", "Regression"],
      "Linear Programming": ["Graphical Method", "Feasible Region", "Optimization", "Word Problems"],
      "Profit & Loss": ["Advanced Problems", "Partnership", "Successive Discount"],
      "GST / Tax": ["CGST & SGST", "Input Tax Credit", "Word Problems"],
      "Banking Basics": ["Compound Interest", "Recurring Deposit", "Fixed Deposit", "EMI"]
    }
  }
};

const CLASSES = Array.from({ length: 12 }, (_, i) => i + 1);
const DIFFICULTIES = ["Easy", "Medium", "Hard"];
const QUESTION_TYPES = [
  { value: "mcq", label: "MCQ" },
  { value: "short_answer", label: "Short Answer" },
  { value: "word_problems", label: "Word Problems" },
  { value: "mixed", label: "Mixed" }
];

function getClassRange(classNum) {
  if (classNum <= 5) return "1-5";
  if (classNum <= 8) return "6-8";
  if (classNum <= 10) return "9-10";
  return "11-12";
}

export default function MathsGenerator() {
  const navigate = useNavigate();
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  const [config, setConfig] = useState({
    class_level: "",
    topic: "",
    subtopic: "",
    difficulty: "Medium",
    question_type: "mixed",
    include_answers: true,
    student_name: ""
  });

  const classRange = config.class_level ? getClassRange(parseInt(config.class_level)) : null;
  const rangeData = classRange ? CLASS_TOPICS[classRange] : null;
  const availableTopics = rangeData ? Object.keys(rangeData.topics) : [];
  const availableSubtopics = useMemo(() => {
    if (!rangeData || !config.topic) return [];
    return rangeData.topics[config.topic] || [];
  }, [rangeData, config.topic]);

  const update = (key, value) => {
    setConfig(prev => {
      const next = { ...prev, [key]: value };
      if (key === "class_level") { next.topic = ""; next.subtopic = ""; }
      if (key === "topic") { next.subtopic = ""; }
      return next;
    });
  };

  const handleGenerate = async () => {
    if (!config.class_level || !config.topic) {
      toast.error("Please select class and topic");
      return;
    }
    setGenerating(true);
    setGenStep(1);
    const stepTimer = setTimeout(() => setGenStep(2), 5000);
    const stepTimer2 = setTimeout(() => setGenStep(3), 15000);
    try {
      const response = await axios.post(`${API}/maths-worksheet/generate`, {
        class_level: parseInt(config.class_level),
        topic: config.topic,
        subtopic: config.subtopic || "General",
        difficulty: config.difficulty,
        question_type: config.question_type,
        include_answers: config.include_answers,
        student_name: config.student_name
      }, { responseType: "blob", timeout: 120000 });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const fname = `maths_class${config.class_level}_${config.topic.replace(/\s+/g, "_")}_${config.difficulty}.pdf`;
      link.setAttribute("download", fname);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Worksheet downloaded!");
    } catch (error) {
      let msg = "Failed to generate worksheet. Please try again.";
      if (error.response?.data) {
        try {
          const text = await error.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed.detail) msg = parsed.detail;
        } catch {}
      }
      toast.error(msg);
    } finally {
      clearTimeout(stepTimer);
      clearTimeout(stepTimer2);
      setGenerating(false);
      setGenStep(0);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-10" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="rounded-full" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="TheMatheMind 360" className="h-7 rounded" />
            <div className="flex flex-col">
              <span className="text-sm font-bold font-['Manrope'] leading-tight">TheMatheMind <span className="text-amber-600">360</span></span>
              <span className="text-[9px] text-muted-foreground leading-tight tracking-wider uppercase">A 360&deg; Learning Platform</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 sm:py-10">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <BookOpen className="w-4 h-4" />
            Free Tool
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Manrope'] text-foreground mb-2" data-testid="page-title">
            Maths Question Generator
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground max-w-lg mx-auto">
            Create custom maths practice worksheets in seconds. Generate printable PDF questions for Class 1–12.
          </p>
        </div>

        <Card className="border-border/50 shadow-lg">
          <CardHeader>
            <CardTitle className="text-lg font-['Manrope']">Configure Your Worksheet</CardTitle>
            <CardDescription>Select class, topic, and preferences</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Class Selection */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Select Class</Label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="class-options">
                {CLASSES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      config.class_level === String(c)
                        ? "border-primary bg-primary/10 text-primary shadow-sm"
                        : "border-border text-muted-foreground hover:border-primary/30"
                    }`}
                    onClick={() => update("class_level", String(c))}
                    data-testid={`class-${c}`}
                  >
                    Class {c}
                  </button>
                ))}
              </div>
              {rangeData && (
                <p className="text-xs text-primary font-medium flex items-center gap-1">
                  <GraduationCap className="w-3 h-3" />
                  {rangeData.label}
                </p>
              )}
            </div>

            {/* Topic */}
            {availableTopics.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Topic</Label>
                <Select value={config.topic} onValueChange={(v) => update("topic", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="topic-select">
                    <SelectValue placeholder="Choose a topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTopics.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Subtopic */}
            {availableSubtopics.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Select Subtopic <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Select value={config.subtopic} onValueChange={(v) => update("subtopic", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="subtopic-select">
                    <SelectValue placeholder="All subtopics" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subtopics</SelectItem>
                    {availableSubtopics.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Difficulty, Type */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Difficulty</Label>
                <Select value={config.difficulty} onValueChange={(v) => update("difficulty", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="difficulty-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIFFICULTIES.map((d) => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Question Type</Label>
                <Select value={config.question_type} onValueChange={(v) => update("question_type", v)}>
                  <SelectTrigger className="rounded-xl" data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUESTION_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Student Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Student Name <span className="text-muted-foreground font-normal">(optional)</span></Label>
              <Input
                data-testid="student-name-input"
                placeholder="Enter student name"
                className="rounded-xl"
                value={config.student_name}
                onChange={(e) => update("student_name", e.target.value)}
              />
            </div>

            {/* Include Answers */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/20">
              <div>
                <Label className="text-sm font-medium">Include Answers</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Adds answer key on a separate page</p>
              </div>
              <Switch
                data-testid="answers-toggle"
                checked={config.include_answers}
                onCheckedChange={(v) => update("include_answers", v)}
              />
            </div>

            {/* Generate Button */}
            <Button
              data-testid="generate-btn"
              className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium"
              onClick={handleGenerate}
              disabled={generating || !config.class_level || !config.topic}
            >
              {generating ? (
                <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{genStep <= 1 ? "Starting AI..." : genStep === 2 ? "AI is writing questions..." : "Building your PDF..."}</>
              ) : (
                <><Download className="w-5 h-5 mr-2" />Generate &amp; Download PDF</>
              )}
            </Button>

            {/* Progress indicator */}
            {generating && (
              <div className="space-y-3 p-4 rounded-xl bg-muted/30 border border-border/50" data-testid="gen-progress">
                <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-1000 ease-out"
                    style={{ width: genStep <= 1 ? "15%" : genStep === 2 ? "60%" : "90%" }}
                  />
                </div>
                <div className="space-y-1.5">
                  <div className={`flex items-center gap-2 text-sm ${genStep >= 1 ? "text-foreground" : "text-muted-foreground"}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${genStep >= 1 ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>1</div>
                    Sending to AI...
                  </div>
                  <div className={`flex items-center gap-2 text-sm ${genStep >= 2 ? "text-foreground" : "text-muted-foreground"}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${genStep >= 2 ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>2</div>
                    AI is writing 10 unique questions with solutions...
                  </div>
                  <div className={`flex items-center gap-2 text-sm ${genStep >= 3 ? "text-foreground" : "text-muted-foreground"}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold ${genStep >= 3 ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}>3</div>
                    Formatting PDF for download...
                  </div>
                </div>
                <p className="text-xs text-muted-foreground text-center">
                  This usually takes 10–20 seconds
                </p>
              </div>
            )}

            {/* Summary */}
            {config.class_level && config.topic && (
              <div className="text-center p-4 rounded-xl bg-muted/30 border border-border/50">
                <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
                  <FileText className="w-4 h-4" />
                  <span>Class {config.class_level}</span>
                  <span>·</span>
                  <span>{config.topic}</span>
                  {config.subtopic && config.subtopic !== "all" && <><span>·</span><span>{config.subtopic}</span></>}
                  <span>·</span>
                  <span>10 Qs</span>
                  <span>·</span>
                  <span>{config.difficulty}</span>
                  <span>·</span>
                  <span>{QUESTION_TYPES.find(t => t.value === config.question_type)?.label}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
